import { Injectable, inject } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import {
    AbstractControl,
    UntypedFormControl,
    ValidationErrors
} from '@angular/forms';
import { ModalNotificationComponent } from '../modal-notification/modal-notification.component';
import {
    ModalAskStepComponent,
    ModalAskStepData
} from '../modal-ask-step/modal-ask-step.component';
import { firstValueFrom } from 'rxjs';
type Color = 'success' | 'info' | 'warning' | 'error';
import { v4 as uuid } from 'uuid';
import { RESPONSIBLES_ORDER } from '@app/constants/RESPONSIBLES_ORDER';
import {
    Address,
    Client,
    ExternalApis,
    OrderCards,
    OrderProcessStructure,
    PrintedRfq,
    Producto,
    Proposals,
    Provider,
    Rfp,
    Rfq,
    RfqAmazon,
    RfqPop,
    Category,
    ProductInOrder,
    ProductInOrderInput
} from '@app/core/services/api/API.service';
import { MatDialog } from '@angular/material/dialog';
import { CredentialsCurrentUser } from '@app/core';
import { environment } from '@env/environment';
import { DetailProduct, DetailVariant } from '../interfaces';
import { CategoryNode } from './serverless/brandfactory-serverless.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export function sinDiacriticos(cadena: string | null | undefined): string {
    if (cadena == null || typeof cadena !== 'string') {
        return '';
    }
    return cadena
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\p{Extended_Pictographic}/gu, '')
        .replace(/[\uFE0F\u200D]/g, '')
        .replace(/[^\p{L}\p{N}\s.\-]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

export function calculateProductName(
    product: DetailProduct,
    actionNewPlastic: boolean,
    balancePurseAmount: number
): string {
    let productName = sinDiacriticos(product.name);
    if (product.isPurse) {
        const price = balancePurseAmount;
        if (
            actionNewPlastic &&
            product?.cardBaseAmmount &&
            product?.cardBaseAmmount > 0
        ) {
            if (price > 0) {
                productName += ' (plástico más saldo)';
            } else {
                productName += ' (plástico)';
            }
        } else {
            if (price > 0) {
                productName += ' (saldo)';
            }
        }
    }
    return productName;
}

export function createProductInput(
    detailProduct: DetailProduct,
    variant: DetailVariant = null,
    print: number = 0,
    printPrice: number = 0,
    quantity: number = 0,
    actionNewPlastic: boolean,
    balancePurseAmount: number
): ProductInOrder {
    return {
        product_id: detailProduct.id,
        provider_id: detailProduct.providerId || null,
        print,
        printPrice,
        product_name: calculateProductName(
            detailProduct,
            actionNewPlastic,
            balancePurseAmount
        ),
        description: detailProduct.long_description ?? null,
        category: detailProduct?.category?.id ?? null,
        unspsc: detailProduct.unspsc || '80141605',
        price:
            !detailProduct.discountPercent || detailProduct.discountPercent < 1
                ? calculateProductPrice(
                      detailProduct,
                      variant,
                      actionNewPlastic,
                      balancePurseAmount
                  )
                : 0,
        sku: detailProduct.sku || null,
        fee: detailProduct.feePercent || 0,
        image: detailProduct.images.slice(0, 1) || null,
        variant: variant
            ? createVariantInput(
                  detailProduct,
                  variant,
                  actionNewPlastic,
                  balancePurseAmount
              )
            : null,
        quantity: quantity,
        originalPrice:
            detailProduct.discountPercent === 1
                ? calculateOriginalPrice(
                      detailProduct,
                      variant,
                      actionNewPlastic
                  )
                : null,
        discount:
            detailProduct.discount !== undefined &&
            detailProduct.discount !== null
                ? parseFloat(detailProduct.discount.toFixed(2))
                : null,
        __typename: 'ProductInOrder'
    };
}

/**
 * Convierte una línea creada con createProductInput en una línea de kit:
 * precio de kit, nombre "Kit de n piezas - …" y SKU con prefijo KIT-n-.
 * La variante conserva su sku original (se usa para empatar stock/variantes),
 * pero su nombre y precio se alinean con el kit porque el carrito y las órdenes
 * los muestran/usan antes que los de la línea.
 */
export function applyKitToProductInput<
    T extends ProductInOrder | ProductInOrderInput
>(
    productInput: T,
    detailProduct: DetailProduct,
    variant: DetailVariant = null
): T {
    const pieces = detailProduct.kitPieces;
    const kitPrice = detailProduct.kitPrice;
    const baseName = variant?.name || productInput.product_name;
    const kitName = sinDiacriticos(`Kit de ${pieces} piezas - ${baseName}`);
    const baseSku = variant?.sku || productInput.sku;

    return {
        ...productInput,
        price: kitPrice,
        originalPrice: null,
        sku: baseSku ? `KIT-${pieces}-${baseSku}` : baseSku,
        product_name: kitName,
        variant: productInput.variant
            ? {
                  ...productInput.variant,
                  name: kitName,
                  price: kitPrice
              }
            : productInput.variant,
        sellMode: detailProduct.sellMode,
        kitPieces: pieces
    } as T;
}

function createVariantInput(
    product: DetailProduct,
    variant: DetailVariant,
    actionNewPlastic: boolean,
    balancePurseAmount: number
): DetailVariant {
    return {
        sku: variant.sku,
        name: sinDiacriticos(variant.name),
        description: sinDiacriticos(variant.description),
        price: calculateProductPrice(
            product,
            variant,
            actionNewPlastic,
            balancePurseAmount
        ),
        filterColors: variant.filterColors,
        image: variant.image.slice(0, 1),
        stock_available: variant.stock_available,
        __typename: 'Variants'
    };
}

export function calculateOriginalPrice(
    product: DetailProduct,
    variant: DetailVariant,
    actionNewPlastic: boolean
): number {
    let unitPriceWithIncrement = product.price;
    if (product.isPurse) {
        if (actionNewPlastic) {
            unitPriceWithIncrement = product.cardBaseAmmount;
        } else {
            unitPriceWithIncrement;
        }
    } else if (variant) {
        unitPriceWithIncrement = variant.price;
    } else {
        unitPriceWithIncrement = product.price;
    }

    return parseFloat(unitPriceWithIncrement.toFixed(2));
}

export function calculateProductPrice(
    product: DetailProduct,
    variant: DetailVariant,
    actionNewPlastic: boolean,
    balancePurseAmount: number
): number {
    if (product.discountPercent === 1) {
        return 0;
    }

    let unitPriceWithDiscount = product.price;
    const discountPercent = product.discountPercent
        ? product.discountPercent
        : 0;

    if (product.isPurse) {
        if (actionNewPlastic) {
            const discount = product.cardBaseAmmount * discountPercent;
            const cardBaseAmmount = product.cardBaseAmmount - discount;
            unitPriceWithDiscount = cardBaseAmmount + balancePurseAmount;
        } else {
            unitPriceWithDiscount =
                balancePurseAmount != null ? balancePurseAmount : 0;
        }
    } else if (variant) {
        const discount = variant.price * discountPercent;
        unitPriceWithDiscount = variant.price - discount;
    } else {
        const discount = product.price * discountPercent;
        unitPriceWithDiscount = product.price - discount;
    }

    return parseFloat((unitPriceWithDiscount || 0).toFixed(2));
}

export function getRandomToast(toastArray: any[]) {
    if (!Array.isArray(toastArray) || toastArray.length === 0) {
        throw new Error('El parámetro debe ser un array no vacío');
    }

    const randomIndex = Math.floor(Math.random() * toastArray.length);
    return toastArray[randomIndex];
}

/**
 * Build a flat list of Category items from a CategoryNode tree, using hierarchical names
 */
export function buildCategoriesFromTree(tree: CategoryNode[]): Category[] {
    const result: Category[] = [];
    const traverse = (node: CategoryNode, ancestors: string[]) => {
        const pathNames = [...ancestors, node.name];
        const category: Category = ({
            __typename: 'Category',
            id: node.id,
            name: pathNames.join(' / '),
            image: null,
            updatedAt: new Date().toISOString(),
            path: (node as any).path ?? null,
            depth: pathNames.length - 1
        } as unknown) as Category;

        result.push(category);
        if (node.children && node.children.length) {
            node.children.forEach(child => traverse(child, pathNames));
        }
    };

    (tree || []).forEach(root => traverse(root, []));
    return sortJSON(result, 'name', 'asc');
}

export function isImageStr(key: string): boolean {
    return /\.(jpg|png|gif|jpeg|tiff|webp|jp2|bmp|jfif)$/i.test(key);
}

export function isANumber(variable: any): boolean {
    return variable instanceof Number ? true : false;
}

export function getPricesVariants(
    variants: DetailVariant[] = [],
    discount: boolean = false
): string {
    try {
        // Verifica si variants es un array válido con al menos un elemento
        if (!Array.isArray(variants) || variants.length === 0) {
            return 'No hay variantes disponibles';
        }

        // Filtra variantes que no tienen la estructura esperada
        const validVariants: DetailVariant[] = variants.filter(
            (variant: DetailVariant): boolean =>
                typeof variant === 'object' &&
                variant !== null &&
                (typeof variant.price === 'number' ||
                    typeof variant.discount === 'number')
        );

        // Si no hay variantes válidas después de filtrar, regresa un mensaje adecuado
        if (validVariants.length === 0) {
            return 'No hay variantes válidas disponibles';
        }

        let cheaper: number = 0;
        let moreExpensive: number = 0;

        if (discount) {
            cheaper = validVariants[0]?.discount ?? 0;
            moreExpensive = validVariants[0]?.discount ?? 0;
            for (let i = 1; i < validVariants.length; i++) {
                const currentDiscount: number = validVariants[i]?.discount ?? 0;
                if (currentDiscount < cheaper) {
                    cheaper = currentDiscount;
                }
                if (currentDiscount > moreExpensive) {
                    moreExpensive = currentDiscount;
                }
            }
        } else {
            cheaper = validVariants[0]?.price ?? 0;
            moreExpensive = validVariants[0]?.price ?? 0;
            for (let i = 1; i < validVariants.length; i++) {
                const currentPrice: number = validVariants[i]?.price ?? 0;
                if (currentPrice < cheaper) {
                    cheaper = currentPrice;
                }
                if (currentPrice > moreExpensive) {
                    moreExpensive = currentPrice;
                }
            }
        }

        const factor: number = this._cardPaymentService?.getCardPayment()
            ? 1.16
            : 1;

        const adjustedMin: number = cheaper * factor;
        const adjustedMax: number = moreExpensive * factor;

        return `$ ${adjustedMin.toFixed(2)} - $ ${adjustedMax.toFixed(2)}`;
    } catch (error) {
        console.error('Error in getPricesVariants:', error);
        return '$ 0.00 - $ 0.00';
    }
}

export function getVolumetricWeight(
    width: number,
    height: number,
    depth: number
): number {
    return (width * height * depth) / 5000;
}

export function toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, function(txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function convertDate(dateString: Date): string {
    const year = dateString.getFullYear(); // Obtiene el año
    const month = String(dateString.getMonth() + 1).padStart(2, '0'); // Obtiene el mes, +1 porque en JS los meses empiezan desde 0
    const day = String(dateString.getDate()).padStart(2, '0'); // Obtiene el día

    // Formato 'yyyy-MM-dd'
    return `${year}-${month}-${day}`;
}

export function compareArrays(array1: string[], array2: string[]) {
    // Ordenamos cada uno de los arrays
    const array1Ordenado = array1.sort();
    const array2Ordenado = array2.sort();

    // Unimos cada array ordenado en una cadena
    const cadenaArray1 = array1Ordenado.join('');
    const cadenaArray2 = array2Ordenado.join('');

    // Comparamos las cadenas resultantes
    return cadenaArray1 === cadenaArray2;
}

/**
 * Recibe un array de strings y retorna este en un string concatendando cada palabra con "," y la última con "y" o "e" de acuerdo a ortografía
 * @param {string[]} array - El array que se desea transformar.
 */
export function joinArrayAnd(array: string[]) {
    // Si el array está vacío, no hay nada que modificar
    if (array.length === 0) {
        return '';
    }

    // Obtenemos una copia del array original
    const copiaArray = [...array];

    // Recorremos el array desde el penúltimo elemento hasta el primero
    for (let i = copiaArray.length - 2; i >= 0; i--) {
        // Obtenemos la siguiente palabra
        const siguientePalabra = copiaArray[i + 1];

        // Verificamos si la siguiente palabra inicia con "i"
        if (siguientePalabra[0].toLowerCase() === 'i') {
            // Si inicia con "i", cambiamos el separador a "e"
            copiaArray[i] = `${copiaArray[i]}e ${siguientePalabra}`;
        } else {
            // Si no inicia con "i", mantenemos el separador ","
            copiaArray[i] = `${copiaArray[i]}, ${siguientePalabra}`;
        }
    }

    // Unimos el array modificado con una coma al final
    const resultado = copiaArray.join(', ');

    // Devolvemos el resultado
    return resultado;
}

export function whitespaceValidator(
    control: AbstractControl
): ValidationErrors | null {
    if (control.value && control.value.trim() === '') {
        return { whitespace: true };
    }
    return null;
}

export function replaceFileNameWithUUID(
    userId: string,
    fileName: string
): string {
    const extension = fileName.split('.').pop();
    const newFileName = `${userId}/${uuid()}.${extension}`;
    return newFileName;
}

export function getUUID(): string {
    return uuid();
}

export function getFileNameByUUID(uuid: string, fileName: string): string {
    const extension = fileName.split('.').pop();
    const newFileName = `${uuid}.${extension}`;
    return newFileName;
}

export function noWhitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
}

export function visiblePremiumView(
    currentProcess: OrderProcessStructure,
    currentUser: CredentialsCurrentUser,
    viewName: string
) {
    const card = currentProcess?.cards?.find(
        (poc: OrderCards) => poc.id === viewName
    );
    if (card) {
        return card?.permissions?.find(
            permission => permission.rol === currentUser.role
        )?.visible;
    } else {
        return false;
    }
}

export function formatAddress(address: Address): string {
    let add = `${address.street} ${address.outdoor_number},`;
    if (address.inside_number) {
        add = `${add} ${address.inside_number},`;
    }
    add = `${add} ${address.colony}, ${address.postal_code}, ${address.delegation}, ${address.state}`;
    if (address.city) {
        add = `${add}, ${address.city}`;
    }
    return add;
}

function hasRequiredAddressText(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.trim() !== '';
}

function getAddressFieldText(value: string | null | undefined): string {
    return typeof value === 'string' ? value.trim() : '';
}

/** Dirección esqueleto guardada previamente con cadenas vacías o null. */
export function isEmptyDeliveryAddress(
    address: Partial<Address> | null | undefined
): boolean {
    if (!address) {
        return true;
    }

    return (
        !getAddressFieldText(address.street) &&
        !getAddressFieldText(address.outdoor_number) &&
        !getAddressFieldText(address.colony) &&
        !getAddressFieldText(address.postal_code) &&
        !getAddressFieldText(address.delegation) &&
        !getAddressFieldText(address.state) &&
        !getAddressFieldText(address.city)
    );
}

export function isValidDeliveryAddress(
    address: Partial<Address> | null | undefined
): boolean {
    if (!address || isEmptyDeliveryAddress(address)) {
        return false;
    }

    const delegation =
        getAddressFieldText(address.delegation) ||
        getAddressFieldText(address.city);
    const outdoorNumber =
        getAddressFieldText(address.outdoor_number) ||
        (getAddressFieldText(address.street) ? 'S/N' : '');

    return (
        hasRequiredAddressText(address.street) &&
        outdoorNumber !== '' &&
        hasRequiredAddressText(address.colony) &&
        hasRequiredAddressText(address.postal_code) &&
        delegation !== '' &&
        hasRequiredAddressText(address.state)
    );
}

export function getValidDeliveryAddresses(
    addresses: Array<Partial<Address>> | null | undefined
): Partial<Address>[] {
    return (addresses ?? []).filter(isValidDeliveryAddress);
}

export function hasValidDeliveryAddresses(
    addresses: Array<Partial<Address>> | null | undefined
): boolean {
    return getValidDeliveryAddresses(addresses).length > 0;
}

/** Campos permitidos en ProductInOrderInput (GraphQL). Cualquier otro campo debe omitirse al enviar. */
const PRODUCT_IN_ORDER_INPUT_KEYS = [
    'product_id',
    'provider_id',
    'index',
    'print',
    'printPrice',
    'product_name',
    'description',
    'category',
    'unspsc',
    'price',
    'originalPrice',
    'sample_requested',
    'sample_request',
    'sample_authorize',
    'sample_authorized',
    'quality_requested',
    'quality_request',
    'uploadedSample',
    'quantity',
    'isExternal',
    'variant',
    'image',
    'sku',
    'qualityForm',
    'fee',
    'increment',
    'discount',
    'logs'
] as const;

const VARIANTS_INPUT_KEYS = [
    'sku',
    'name',
    'description',
    'colors',
    'filterColors',
    'image',
    'stock_available',
    'price',
    'size'
] as const;

const FILE_INPUT_KEYS = [
    'key',
    'url',
    'color',
    'palette',
    'colorName'
] as const;

function pickInputVariant(obj: any): any {
    if (obj == null) return null;
    const out: Record<string, unknown> = {};
    for (const key of VARIANTS_INPUT_KEYS) {
        if (
            Object.prototype.hasOwnProperty.call(obj, key) &&
            obj[key] !== undefined
        ) {
            if (key === 'image' && Array.isArray(obj[key])) {
                out[key] = obj[key].map((f: any) => pickFileInput(f));
            } else {
                out[key] = obj[key];
            }
        }
    }
    return out;
}

function pickFileInput(obj: any): any {
    if (obj == null) return null;
    const out: Record<string, unknown> = {};
    for (const key of FILE_INPUT_KEYS) {
        if (
            Object.prototype.hasOwnProperty.call(obj, key) &&
            obj[key] !== undefined
        ) {
            out[key] = obj[key];
        }
    }
    return out;
}

/**
 * Convierte un producto (API o createProductInput) al formato ProductInOrderInput,
 * eliminando campos no definidos en el esquema (ej. __typename, subcategory, formatQA).
 */
export function toProductInOrderInput(product: any): any {
    if (product == null) return product;
    const out: Record<string, unknown> = {};
    for (const key of PRODUCT_IN_ORDER_INPUT_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(product, key)) continue;
        const value = product[key];
        if (value === undefined) continue;
        if (key === 'variant') {
            out[key] = pickInputVariant(value);
        } else if (key === 'image' && Array.isArray(value)) {
            out[key] = value.map((f: any) => pickFileInput(f));
        } else {
            out[key] = value;
        }
    }
    return out;
}

export function removeTypename(obj: any): any {
    if (Array.isArray(obj)) {
        // Si es un arreglo, recorre sus elementos de manera recursiva
        return obj.map(el => removeTypename(el));
    } else if (typeof obj === 'object' && obj !== null) {
        // Si es un objeto, elimina el atributo __typename si existe
        if (obj.hasOwnProperty('__typename')) {
            delete obj['__typename'];
        }
        // Elimina el campo novelty si existe (no está definido en el esquema GraphQL)
        if (obj.hasOwnProperty('novelty')) {
            delete obj['novelty'];
        }
        // Luego, recorre las propiedades del objeto de manera recursiva
        for (let prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                obj[prop] = removeTypename(obj[prop]);
            }
        }
    }
    return obj;
}

export function capitalizeFirstLetter(inputString: any) {
    return (
        inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase()
    );
}

export async function sleep(millis: number) {
    return new Promise(resolve => setTimeout(resolve, millis));
}

export function getNameResonsible(id: number): string {
    const item = RESPONSIBLES_ORDER.find(responsible => responsible.id === id);
    if (item) {
        return item.name;
    }
    return 'RESPONSABLE NO ENCONTRADO';
}

export function formatSimpleDate(date: Date): string {
    const days = [
        'domingo',
        'lunes',
        'martes',
        'miércoles',
        'jueves',
        'viernes',
        'sábado'
    ];
    const months = [
        'enero',
        'febrero',
        'marzo',
        'abril',
        'mayo',
        'junio',
        'julio',
        'agosto',
        'septiembre',
        'octubre',
        'noviembre',
        'diciembre'
    ];

    const dayOfWeek = days[date.getDay()];
    const dayOfMonth = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${dayOfWeek} ${dayOfMonth} de ${month} de ${year} a las ${hours}:${
        minutes < 10 ? '0' : ''
    }${minutes}`;
}

export function getNameProviderClient(
    id: string,
    providers: (Client | Provider)[]
): string {
    const item = providers.find(provider => provider.id === id);
    if (item) {
        return item.comertial_name;
    }
    return 'EMPRESA NO ENCONTRADA';
}

export function getCategoryObject(
    id: string,
    categories: Category[]
): Category {
    const item = categories.find(category => category.id === id);
    if (item) {
        return item;
    }
    return null;
}

export function getClientLogo(id: string, clients: Client[]): string {
    const item = clients.find(client => client.id === id);
    if (item) {
        return `${environment.urlImages}${item.logo.key}`;
    }
    return 'https://pico-imports-assets.s3.amazonaws.com/noimage.jpg';
}

export function getGrayClientLogo(id: string, clients: Client[]): string {
    const item = clients.find(client => client.id === id);
    if (item?.grayLogo?.key) {
        return `${environment.urlImages}${item.grayLogo.key}`;
    }
    return 'https://pico-imports-assets.s3.amazonaws.com/noimage.jpg';
}

export function getNameCategorySubcategory(
    id: string,
    categories: Category[]
): string {
    if (!categories || !Array.isArray(categories)) {
        return 'Cargando...';
    }
    const item = categories.find(category => category.id === id);
    if (item) {
        return item.name;
    }
    return 'ITEM NO ENCONTRADO';
}

export function getRandomItemFromArray(array: any[]) {
    // Validar si el array es vacío
    if (array.length === 0) {
        throw new Error('El array no puede estar vacío');
    }

    // Obtener un índice aleatorio dentro del rango del array
    const indiceAleatorio = Math.floor(Math.random() * array.length);

    // Devolver el elemento aleatorio del array
    return array[indiceAleatorio];
}

export interface FilterPremiums {
    searchTerm: string;
    sort: 'default' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';
    filterColors: string[];
    category: string;
    price: string;
    client: string;
    provider: string;
    externalAPI: string;
    isAllowable: boolean;
    isPreapproved: boolean;
    isRestricted: boolean;
    status: string;
}

export interface FilterRfqs {
    searchTerm: string;
    estatus: 'all' | 'request' | 'completed';
    clasification:
        | 'all'
        | 'nonproposals'
        | 'inprocess'
        | 'inreview'
        | 'pendingorder'
        | 'generatedorder'
        | 'nonorder';
}

export interface TableColumn {
    name: string;
    key: string;
    status: boolean;
    icon?: string;
    subtitle?: string;
}

export function isImage(file: File): boolean {
    return file.type.startsWith('image/');
}

export function rfqMatch(
    rfq: Rfq | RfqPop | PrintedRfq | RfqAmazon | Rfp,
    filter: FilterRfqs
) {
    let visible = true;
    if (filter.estatus === 'request' && rfq.estatus !== 'request') {
        visible = false;
    }
    if (filter.estatus === 'completed' && rfq.estatus !== 'completed') {
        visible = false;
    }

    if (filter.clasification !== 'all') {
        let status = '';
        const proposals: Proposals[] | any = rfq.proposals;
        if (rfq.estatus === 'request') {
            const date = new Date(rfq.createdAt);
            const now = new Date();
            date.setTime(date.getTime() + rfq.limit_time * 60 * 60 * 1000); // Fecha límite
            if (proposals.length === 0) {
                // No hay propuestas
                status = 'nonproposals';
            } else {
                // Ya hay propuestas
                if (now < date) {
                    // Sigue vigente (Aún no se puede declarar ganador)
                    status = 'inprocess';
                } else {
                    // Ya expiró (Ya se puede elegir ganador)
                    if (rfq.winner) {
                        status = 'pendingorder'; // Hay hay ganador
                    } else {
                        status = 'inreview'; // En espera de ganador
                    }
                }
            }
        } else {
            // Completed
            if (rfq.__typename === 'Rfq' && rfq.premiumOrderId) {
                status = 'generatedorder';
            } else {
                status = 'nonorder';
            }
        }
        if (filter.clasification !== status) {
            visible = false;
        }
    }
    if (
        filter.searchTerm !== '' &&
        !sinDiacriticos(rfq.nombre).includes(sinDiacriticos(filter.searchTerm))
    ) {
        visible = false;
    }

    return visible;
}

export function premiumSort(
    a: Producto,
    b: Producto,
    sort: 'default' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc'
) {
    if (sort === 'default') {
        return 0;
    } else if (sort === 'price-asc') {
        return a.price - b.price;
    } else if (sort === 'price-desc') {
        return b.price - a.price;
    } else if (sort === 'name-asc') {
        return a.name.localeCompare(b.name);
    } else if (sort === 'name-desc') {
        return b.name.localeCompare(a.name);
    }
    return 0;
}

export function premiumMatch(
    product: Producto,
    filter: FilterPremiums,
    enabledApis: ExternalApis[]
) {
    let visible = true;
    if (
        filter.searchTerm !== '' &&
        !product.name.toLowerCase().includes(filter.searchTerm.toLowerCase()) &&
        !product.sku.toLowerCase().includes(filter.searchTerm.toLowerCase())
    ) {
        visible = false;
    }
    if (filter.filterColors.length > 0) {
        visible = false;
        if (
            product.filterColors &&
            filter.filterColors.some((color: any) =>
                product.filterColors.includes(color)
            )
        ) {
            visible = true;
        }
    }

    switch (filter.externalAPI) {
        case 'no-api':
            {
                if (product.isExternal && product.nameExternal) {
                    visible = false;
                }
            }
            break;
        case 'all-apis':
            {
                if (!product.isExternal && !product.nameExternal) {
                    visible = false;
                }
            }
            break;
        default:
            {
                if (
                    filter.externalAPI !== 'all' &&
                    filter.externalAPI !== product.nameExternal
                ) {
                    visible = false;
                }
            }
            break;
    }

    if (filter.provider !== 'all' && product.providerId !== filter.provider) {
        visible = false;
    }

    if (filter.status !== 'all' && product.status !== filter.status) {
        visible = false;
    }
    if (filter.isAllowable && !product.isAllowable) {
        visible = false;
    }
    if (filter.isPreapproved && product.pgClasitication !== 'active') {
        visible = false;
    }
    if (filter.isRestricted && product.isRestricted) {
        visible = false;
    }
    const range = {
        from: 0,
        to: 0
    };
    switch (filter.price) {
        case 'all':
            {
                range.from = 0;
                range.to = 0;
            }
            break;
        case '1to50':
            {
                range.from = 1;
                range.to = 50;
            }
            break;
        case '51to100':
            {
                range.from = 51;
                range.to = 100;
            }
            break;
        case '101to500':
            {
                range.from = 101;
                range.to = 500;
            }
            break;
        case '501to1000':
            {
                range.from = 501;
                range.to = 1000;
            }
            break;
        case 'moreThan1000':
            {
                range.from = 1001;
                range.to = 0;
            }
            break;
    }
    if (range.from !== 0) {
        if (range.to === 0) {
            if (product.price < range.from) {
                visible = false;
            }
        } else {
            if (product.price < range.from || product.price >= range.to) {
                visible = false;
            }
        }
    }
    if (
        filter.client !== 'all' &&
        !product.visibleFor.includes(filter.client)
    ) {
        visible = false;
    }
    if (
        filter.client !== 'all' &&
        !product.isByRequest &&
        product.stock === 0
    ) {
        visible = false;
    }
    if (
        filter.client !== 'all' &&
        product.isExternal &&
        product.nameExternal &&
        !enabledApis.find(
            (ea: ExternalApis) => ea.nameExternal === product.nameExternal
        )
    ) {
        visible = false;
    }
    return visible;
}

export function getFechaLimite(start_time: number, limit_time: number): number {
    const fecha = new Date(start_time);
    fecha.setHours(fecha.getHours() + limit_time);
    return fecha.getTime();
}

export function sortJSON(data: any[], key: string, orden: 'asc' | 'desc') {
    return data.sort(function(a: any, b: any) {
        const x = a[key],
            y = b[key];
        if (orden === 'asc') {
            return x < y ? -1 : x > y ? 1 : 0;
        }
        if (orden === 'desc') {
            return x > y ? -1 : x < y ? 1 : 0;
        }
    });
}

export function removeDuplicates(objectsArray: any[], key: string) {
    return objectsArray.filter((obj, pos, arr) => {
        return arr.map(mapObj => mapObj[key]).indexOf(obj[key]) === pos;
    });
}

@Injectable({
    providedIn: 'root'
})
export class UtilService {
    fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    fileExtension = '.xlsx';

    private _matDialog = inject(MatDialog);
    private _matSnackBar = inject(MatSnackBar);
    constructor() {}

    exportExcel(jsonData: any[], fileName: string = 'export.xlsx'): void {
        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
        const wb: XLSX.WorkBook = {
            Sheets: { data: ws },
            SheetNames: ['data']
        };
        const excelBuffer: any = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'array'
        });
        this.saveExcelFile(excelBuffer, fileName);
    }

    exportToCsv(data: any[], fileName: string = 'export.csv') {
        // Crear un blob con el contenido CSV
        const csvData = this.convertArrayToCSV(data);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });

        // Crear un enlace para descargar el archivo
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    private convertArrayToCSV(data: any[]) {
        // Obtener los encabezados de las columnas
        const headers = Object.keys(data[0]);
        // Unir los encabezados con comas
        let csv = headers.join(',');

        // Iterar sobre los datos y agregarlos al CSV
        data.forEach(row => {
            csv += '\n';
            csv += headers
                .map(header => {
                    const cell = row[header];
                    return `"${cell}"`;
                })
                .join(',');
        });
        return csv;
    }

    public sortJSON(data: any[], key: string, orden: string) {
        return data.sort(function(a: any, b: any) {
            const x = a[key],
                y = b[key];
            if (orden === 'asc') {
                return x < y ? -1 : x > y ? 1 : 0;
            }
            if (orden === 'desc') {
                return x > y ? -1 : x < y ? 1 : 0;
            }
        });
    }

    async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    removeDuplicatesId(objectsArray: any[]) {
        return objectsArray.filter((obj, pos, arr) => {
            return arr.map(mapObj => mapObj.id).indexOf(obj.id) === pos;
        });
    }

    removeDuplicates(objectsArray: any[], key: string) {
        return objectsArray.filter((obj, pos, arr) => {
            return arr.map(mapObj => mapObj[key]).indexOf(obj[key]) === pos;
        });
    }

    saveExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: this.fileType });
        FileSaver.saveAs(data, fileName + this.fileExtension);
    }

    noWhitespaceValidator(control: UntypedFormControl) {
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }

    eliminarTypename(obj: any): any {
        if (Array.isArray(obj)) {
            // Si es un arreglo, recorre sus elementos de manera recursiva
            return obj.map(el => this.eliminarTypename(el));
        } else if (typeof obj === 'object' && obj !== null) {
            // Si es un objeto, elimina el atributo __typename si existe
            if (obj.hasOwnProperty('__typename')) {
                delete obj['__typename'];
            }
            // Luego, recorre las propiedades del objeto de manera recursiva
            for (let prop in obj) {
                if (obj.hasOwnProperty(prop)) {
                    obj[prop] = this.eliminarTypename(obj[prop]);
                }
            }
        }
        return obj;
    }

    isNullOrUndefined(value: any): value is null | undefined {
        return value === null || value === undefined;
    }

    formatCurrency(amount: number, currencyCode: string = 'USD'): string {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode
        });
        return formatter.format(amount);
    }

    /**
     * Muestra un toast con el mensaje especificado.
     * @param {string} msg - El mensaje que se mostrará en el toast.
     * @param {Color} color - El color del toast ('success', 'info', 'warning', 'error').
     * @param {number} duration - La duración del toast en milisegundos.
     * @param {any} [multiline=null] - Opción para habilitar el modo multilinea.
     * @param {string} [iconClass=null] - Clases Tailwind para el icono (p. ej. 'text-warning'). Reemplaza el color por defecto.
     */
    presentToast(
        msg: string,
        color: Color,
        duration: number,
        multiline: any = null,
        iconClass: string | null = null
    ) {
        const data: any = {
            message: msg,
            color,
            duration
        };
        if (multiline) {
            data.multiline = multiline;
        }
        this._matDialog.open(ModalNotificationComponent, {
            autoFocus: false,
            data: {
                message: msg,
                color,
                duration,
                multiline: multiline ?? null,
                iconClass: iconClass ?? null
            }
        });
    }

    /**
     * Abre {@link ModalAskStepComponent} para una confirmación sí/no.
     * @param data Título, mensaje y etiquetas de los botones (cancelar / confirmar).
     * @returns Solo devuelve `true` si el diálogo se cierra con el valor estricto `true`
     * (botón afirmativo). En cualquier otro caso (`false`, `undefined`, cierre con X, backdrop, etc.) devuelve `false`.
     */
    confirmAskStep(data: ModalAskStepData): Promise<boolean> {
        const dialogRef = this._matDialog.open(ModalAskStepComponent, {
            autoFocus: false,
            data
        });
        return firstValueFrom(dialogRef.afterClosed()).then(
            (result: unknown): boolean => result === true
        );
    }

    presentSnackBar(msg: string, color: Color) {
        const snackBar = this._matSnackBar.open(msg, 'Cerrar', {
            duration: 5000,
            panelClass: color
        });
    }

    /**
     * Decode JWT token and extract buyerCookie
     * @param token The JWT token to decode
     * @returns The buyerCookie value or null if not present
     */
    decodeTokenAndGetBuyerCookie(token: string): string | null {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(function(c) {
                        return (
                            '%' +
                            ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                        );
                    })
                    .join('')
            );

            const payload = JSON.parse(jsonPayload);
            return payload.buyerCookie || null;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }
}
